import { useEffect, useRef, useState } from 'react';

/**
 * MoreActionsMenu — anchored dropdown for secondary bulk actions.
 *
 * Renders the "More ▾" button + a menu listing actions with surface:'more'.
 * Each menu item, when clicked, calls onPick(action) — the parent
 * (BulkActionBar) routes to either an inline handler or a popover.
 *
 * Props:
 *   actions  — Array<action def> (the secondary set)
 *   busy     — bool; disables the menu while an action is running
 *   onPick   — (action) => void
 */
export default function MoreActionsMenu({ actions, busy = false, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  if (actions.length === 0) return null;

  return (
    <div className="bulk-more-wrap">
      <button
        ref={btnRef}
        type="button"
        className="bulk-action-btn bulk-action-btn--secondary"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
      >
        More <span className="bulk-more-caret">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div ref={ref} className="bulk-more-menu" role="menu">
          {actions.map((a) => (
            <button
              key={a.key}
              type="button"
              className="bulk-more-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onPick?.(a);
              }}
              disabled={busy}
            >
              <span className="bulk-more-item-icon">{a.icon}</span>
              <span className="bulk-more-item-label">{a.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
